/**
 * OrderDocumentsGenerateBar
 *
 * Floating pill reporting the order-document generation lifecycle
 * (`loading → queued → error`), fixed to the bottom-centre of the viewport.
 * Mirrors the generate modes of the events home `EventsSelectionDock`
 * (GRAIN-2326 follows GRAIN-1488's feedback pattern): one mounted pill that
 * morphs between modes — no swapping elements.
 *
 * | mode      | content                                                   |
 * |-----------|-----------------------------------------------------------|
 * | `loading` | spinner + "Generating document"                           |
 * | `queued`  | check + "Queued: …" (link opens the Activity Centre)      |
 * |           |   + dismiss × + auto-dismiss timer                        |
 * | `error`   | red × + error copy + dismiss × (no auto-dismiss)          |
 *
 * Reuses the shared bulk-action chrome from `BulkActionBar` so it reads
 * identically to the events dock and the products bulk bars.
 */

// Icons
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { cn } from '@/lib/utils';
import { CancelTagIcon } from '@icons/CancelTagIcon';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';
import { CloseIcon } from '@icons/CloseIcon';
import { LoadingSpinnerIcon } from '@icons/LoadingSpinnerIcon';

// Libraries
import { AnimatePresence } from 'framer-motion';

// Shared chrome
import {
  BulkActionBar,
  DismissProgressBar,
  FadeIn,
} from '@/components/ui/bulk-action-bar';

// Hooks
import { useTranslation } from '@hooks/useTranslation';


const LOADING_ICON = <LoadingSpinnerIcon className="size-5 shrink-0 text-off-black/60" />;
const QUEUED_ICON = <CheckCircleIcon className="size-5 shrink-0 text-yield-positive" aria-hidden="true" />;
const ERROR_ICON = <CancelTagIcon className="shrink-0 text-chilli-red" />;

export type OrderDocumentsGenerateBarMode = 'loading' | 'queued' | 'error';

/**
 * Copy the bar renders per mode. Defaults to the order-document strings
 * (`order_actions.generate_*`). A caller generating a different document family
 * (e.g. per-event event orders) overrides only the strings that differ; the
 * generic "Queued" / "Dismiss" words fall through to the defaults.
 */
export interface OrderDocumentsGenerateBarCopy {
  loading: string;
  queuedLink: string;
  queuedRest: string;
  error: string;
  dismiss: string;
}

export interface OrderDocumentsGenerateBarProps {
  /** Current mode, or null when the bar is hidden. */
  mode: OrderDocumentsGenerateBarMode | null;
  /** Fires on the × dismiss button (queued or error mode). */
  onDismiss: () => void;
  /** Fires when the queued auto-dismiss timer completes. */
  onAutoDismiss: () => void;
  /** Fires when the underlined "Queued" word is clicked — opens the Activity Centre. */
  onQueuedLinkClick: () => void;
  /** Per-mode copy overrides; unset keys fall back to the order-document strings. */
  copy?: Partial<OrderDocumentsGenerateBarCopy>;
}

export function OrderDocumentsGenerateBar({
  mode,
  onDismiss,
  onAutoDismiss,
  onQueuedLinkClick,
  copy,
}: OrderDocumentsGenerateBarProps) {
  const { t } = useTranslation();

  // The success copy is split across two keys so the interactive lead word
  // ("Queued" — an underlined link to the Activity Centre) is locale-independent
  // of the plain remainder (": Order document" / ": Event order documents").
  const c: OrderDocumentsGenerateBarCopy = {
    loading: copy?.loading ?? t('order_actions.generate_loading'),
    queuedLink: copy?.queuedLink ?? t('order_actions.generate_success_link'),
    queuedRest: copy?.queuedRest ?? t('order_actions.generate_success_rest'),
    error: copy?.error ?? t('order_actions.generate_error'),
    dismiss: copy?.dismiss ?? t('order_actions.generate_dismiss'),
  };

  const ariaLabel =
    mode === 'loading' ? c.loading
    : mode === 'queued' ? `${c.queuedLink}${c.queuedRest}`
    : c.error;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-16 z-50 flex justify-center">
      <AnimatePresence>
        {mode && (
          <BulkActionBar key="order-documents-generate-bar" ariaLabel={ariaLabel} className="gap-6">
            <FadeIn keyId={mode}>
              {mode === 'loading' && (
                <span className="flex items-center gap-2">
                  {LOADING_ICON}
                  <span className="whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
                    {c.loading}
                  </span>
                </span>
              )}

              {mode === 'queued' && (
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
                    {QUEUED_ICON}
                    <span className="inline-flex items-center">
                      <button
                        type="button"
                        onClick={onQueuedLinkClick}
                        className={cn('cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', UNDERLINE_STATIC)}
                      >
                        {c.queuedLink}
                      </button>
                      {c.queuedRest && <span>{c.queuedRest}</span>}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label={c.dismiss}
                    className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <CloseIcon className="size-3" />
                  </button>
                </div>
              )}

              {mode === 'error' && (
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-[6px]">
                    {ERROR_ICON}
                    <span className="whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
                      {c.error}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label={c.dismiss}
                    className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <CloseIcon className="size-3" />
                  </button>
                </div>
              )}
            </FadeIn>

            {mode === 'queued' && (
              <DismissProgressBar onComplete={onAutoDismiss} />
            )}
          </BulkActionBar>
        )}
      </AnimatePresence>
    </div>
  );
}
